import React from "react";
import { Alert, Col, Form } from "react-bootstrap";
import FieldHelpTooltip from "../../../../components/Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../../../../components/Help/HelpContent";
import PartsUsedSection from "../../PartsUsedSection";
import StagedPartsSection from "../../StagedPartsSection";
import ServiceSpecificsSection from "../../ServiceSpecificsSection";

const Required = () => <span style={{ color: "red", fontSize: "1rem" }}> *</span>;

// The icon a vendor uploaded under Branding & Offers for a service / sub category / service type.
// Renders nothing when there is none (or it fails to load), so items without an icon look as before.
function ItemIcon({ src, size = 22 }) {
  const [broken, setBroken] = React.useState(false);
  if (!src || broken) return null;
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      onError={() => setBroken(true)}
      style={{ width: size, height: size, borderRadius: 6, objectFit: "cover", flexShrink: 0 }}
    />
  );
}

const pillWithIcon = { display: "inline-flex", alignItems: "center", gap: 8 };

// Step 2: what is needed - the vehicle / customer asset, the profile, the service category,
// sub category and service type, any subscription to apply, parts and notes.
function ServiceStep({ b, formik }) {
  const {
    isSportsService,
    currency,
    group_id,
    user_id,
    existingData,
    setServiceSpecifics,
    handleServiceSpecificsLoaded,
    partners,
    setPartner_id,
    fetchServiceData,
    selectedService,
    setSelectedService,
    services,
    serviceData,
    groupedData,
    activeType,
    setActiveType,
    serviceTypes,
    addOffering,
    userFlexiSubs,
    selectedSubscription,
    setSelectedSubscription,
    pendingSubRef,
    priceSection,
    addStagedPart,
    removeStagedPart,
    pendingBalanceIncrease,
    originalPartsSubtotalRef,
    partsChangedRef,
    setEditPartsSubtotal,
    additionalNotes,
    setAdditionalNotes
  } = b;
  const { values, errors, touched, handleChange, handleBlur } = formik;

  return (
    <>
      <div className="bstep-card">
        <div className="bstep-card-head">
          <h5>{isSportsService ? "Sports" : "Service"}</h5>
          <p>Pick what the customer needs. You can add offerings from more than one category.</p>
        </div>

        <ServiceSpecificsSection
          customerId={user_id || null}
          bookingId={group_id ? existingData?.[0]?.id : null}
          onChange={setServiceSpecifics}
          onLoaded={handleServiceSpecificsLoaded}
        />

        <div className="mb-4" data-tour="booking-details">
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label>
                Profile
                <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportsprofile : HELP_CONTENT.bookings.profile)} />
              </Form.Label>
              <Form.Select
                name="partner"
                value={values.partner}
                onChange={(e) => {
                  handleChange(e);
                  setPartner_id(e.target.value);
                  fetchServiceData(selectedService, e.target.value);
                }}
                onBlur={handleBlur}
                isInvalid={touched.partner && errors.partner}
                className={`form-input ${values.partner === "" ? "placeholder-style" : ""}`}
                style={{
                  height: "40px",
                  backgroundColor: "white",
                  border: touched.partner && errors.partner ? "1px solid #dc3545" : "1px solid #E9ECEF"
                }}
              >
                <option value="">Select Profile</option>
                {partners?.map((partner) => (
                  <option key={partner?.id} value={partner?.id}>
                    {partner?.partner_name}
                  </option>
                ))}
              </Form.Select>
              {touched.partner && errors.partner && <div className="invalid-feedback d-block">{errors.partner}</div>}
            </Form.Group>
          </Col>

          <h6 className="section-title" data-tour="booking-services">
            {isSportsService ? "Sports" : "Service Category"}
            <Required />
            <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.SportsserviceOfferings : HELP_CONTENT.bookings.serviceOfferings)} />
          </h6>

          {services && services.length > 0 ? (
            <div className="d-flex gap-2 flex-wrap">
              {services.map((ele) => {
                // A category with an added offering stays highlighted after switching away.
                const hasAdded = serviceData?.some((d) => d?.added && String(d?.service_id) === String(ele?.service_id));
                return (
                  <button
                    type="button"
                    key={ele.service_id}
                    onClick={() => setSelectedService(ele)}
                    className={`service-type-pill ${ele?.service_id === selectedService?.service_id || hasAdded ? "active" : ""}`}
                    style={pillWithIcon}
                  >
                    <ItemIcon src={ele?.service_icon} />
                    {ele?.service_name}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center p-4 border rounded bg-light">
              <h6 className="text-danger mb-2">No Services Available</h6>
              <p className="text-muted mb-0">
                {isSportsService
                  ? "Please add Sports & Sports Type from Admin Setup to continue booking."
                  : "Please add Service & Type from Admin Setup to continue booking."}
              </p>
            </div>
          )}
        </div>

        {/* SUB CATEGORY */}
        {!Object.keys(groupedData).includes("null") && (
          <div className="mb-4">
            <h6 className="section-title">
              Sub Category
              <Required />
            </h6>
            <div className="d-flex gap-2 flex-wrap">
              {Object.keys(groupedData).map((ele) => {
                // Show the pill as selected whenever it has an added item, not only the clicked tab.
                const hasAdded = groupedData[ele]?.some((d) => d?.added);
                return (
                  <button
                    type="button"
                    key={ele}
                    onClick={() => setActiveType(ele)}
                    className={`service-type-pill ${ele === activeType || hasAdded ? "active" : ""}`}
                    style={pillWithIcon}
                  >
                    <ItemIcon src={groupedData[ele]?.find((d) => d?.service_type_image)?.service_type_image} />
                    {ele}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* SERVICE TYPE (the offerings) */}
        {services?.length > 0 && (
          <>
            {serviceTypes && serviceTypes.length > 0 ? (
              <div>
                <h6 className="section-title">
                  {isSportsService ? "Sports Type" : "Service Type"}
                  <Required />
                </h6>

                <div className="row g-3">
                  {groupedData?.[activeType]?.map((data) => (
                    <div className="col-sm-6 col-xl-4" key={data.id}>
                      <div
                        onClick={() => addOffering(data)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), addOffering(data))}
                        aria-pressed={!!data?.added}
                        className="offering-row-card d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex align-items-center" style={{ gap: 12, minWidth: 0 }}>
                          <ItemIcon src={data?.service_offering_image} size={40} />
                          <div style={{ minWidth: 0 }}>
                            <div className="fw-semibold">{data?.service_offering_name}</div>
                            <div className="price-text">
                              {currency} {data?.price}
                            </div>
                          </div>
                        </div>

                        <div className={`btn btn-sm rounded-3 ${data?.added ? "btn-danger" : "btn-outline-danger"}`}>
                          {data?.added ? "Added" : "Add"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center p-4 border rounded bg-light">
                <h6 className="text-danger mb-2">No Service Types Available</h6>
                <p className="text-muted mb-0">Please add Service Type under the selected Service to continue booking.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* SUBSCRIPTION */}
      {userFlexiSubs.length > 0 && (
        <div className="bstep-card">
          <h6 className="section-title">
            Apply Subscription <span style={{ fontSize: "12px", fontWeight: "400", color: "#6B7280" }}>(optional)</span>
          </h6>
          <Form.Select
            aria-label="Apply subscription"
            value={selectedSubscription ? selectedSubscription.user_subscription_id : ""}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) {
                setSelectedSubscription(null);
                pendingSubRef.current = null;
                // Reset service information: reload current service with no pre-selection
                fetchServiceData(selectedService);
              } else {
                const sub = userFlexiSubs.find((s) => String(s.user_subscription_id) === val);
                if (sub) {
                  setSelectedSubscription(sub);
                  pendingSubRef.current = sub;
                  const matchedService = services?.find((s) => String(s.service_id) === String(sub.service_id));
                  if (matchedService) {
                    if (String(matchedService.service_id) === String(selectedService?.service_id)) {
                      // Same service already active - fetch directly to apply auto-selection
                      fetchServiceData(matchedService);
                    } else {
                      // Different service - update selectedService, the effect calls fetchServiceData
                      setSelectedService(matchedService);
                    }
                  }
                }
              }
            }}
            style={{ maxWidth: "420px", height: "40px" }}
          >
            <option value="">— None (no subscription) —</option>
            {userFlexiSubs.map((sub) => (
              <option key={sub.user_subscription_id} value={sub.user_subscription_id}>
                {sub.plan_name} — {sub.service_name}
                {sub.service_offering_name ? ` / ${sub.service_offering_name}` : ""}
              </option>
            ))}
          </Form.Select>

          {selectedSubscription && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px 16px",
                background: "#FDE8EF",
                borderRadius: "10px",
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                fontSize: "13px",
                color: "#374151"
              }}
            >
              {[
                { label: "Plan", value: selectedSubscription.plan_name },
                { label: "Service", value: selectedSubscription.service_name },
                selectedSubscription.service_type_name && { label: "Sub Category", value: selectedSubscription.service_type_name },
                selectedSubscription.service_offering_name && { label: "Service Type", value: selectedSubscription.service_offering_name },
                { label: "Pricing", value: `${currency} ${Number(selectedSubscription.pricing || 0).toFixed(2)}` },
                {
                  label: "Sessions",
                  value:
                    selectedSubscription.number_of_sessions === "Limited"
                      ? `${selectedSubscription.total_sessions} sessions`
                      : selectedSubscription.number_of_sessions || "-"
                },
                {
                  label: "Plan End",
                  value: selectedSubscription.plan_end_date ? new Date(selectedSubscription.plan_end_date).toLocaleDateString() : "-"
                }
              ]
                .filter(Boolean)
                .map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontWeight: "600", color: "#C0335A", fontSize: "11px", textTransform: "uppercase", marginBottom: "2px" }}>
                      {label}
                    </div>
                    <div style={{ fontWeight: "600", color: "#111827" }}>{value}</div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* PARTS: a saved booking saves them instantly; a new one stages them until it is created. */}
      <div className="bstep-card">
        {group_id ? (
          <>
            {pendingBalanceIncrease > 0 && (
              <Alert variant="warning" className="mb-3">
                Adding parts increases this booking's balance due by {currency} {pendingBalanceIncrease.toFixed(2)}. Click Update to
                save the new total.
              </Alert>
            )}
            <PartsUsedSection
              bookingId={existingData?.[0]?.id}
              currency={currency}
              onPartsChanged={(subtotal) => {
                // The baseline comes from the dedicated fetch in getDataByID, not from "was this
                // the first call": if it hasn't resolved yet, don't flag a change.
                if (originalPartsSubtotalRef.current !== null && Number(subtotal) !== Number(originalPartsSubtotalRef.current)) {
                  partsChangedRef.current = true;
                }
                setEditPartsSubtotal(subtotal);
              }}
            />
          </>
        ) : (
          <StagedPartsSection
            parts={priceSection.filter((p) => p?.isPart)}
            currency={currency}
            onAdd={addStagedPart}
            onRemove={removeStagedPart}
          />
        )}

        <div className="mt-3">
          <label className="form-label fw-semibold" htmlFor="bstep-notes">
            Additional Notes
            <FieldHelpTooltip {...(isSportsService ? HELP_CONTENT.bookings.Sportsnotes : HELP_CONTENT.bookings.notes)} />
          </label>
          <Form.Control
            id="bstep-notes"
            as="textarea"
            rows={4}
            placeholder="Enter any additional notes or service instructions..."
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            style={{ fontSize: "14px", resize: "none" }}
          />
        </div>
      </div>
    </>
  );
}

export default ServiceStep;
